import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApiError, api, onSessionExpired, setCsrfToken } from './api.js';
import { SessionExpired } from '../components/SessionExpired.js';
import type { StaffFeatures } from '@wms/shared';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  locale: string;
  /** Null when this account has never explicitly chosen an appearance. */
  themePalette: string | null;
  themeMode: string | null;
  roles: { role: string; scopeType: string; scopeId: string | null }[];
  ownerAdmin?: boolean;
  staffRestricted?: boolean;
  staffFeatures?: StaffFeatures;
  staffScopes?: { scopeType: string; scopeId: string; canWrite: boolean }[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(
    () =>
      onSessionExpired(() => {
        // Initial /me failures should still lead directly to the login form.
        if (user) setSessionExpired(true);
      }),
    [user],
  );

  // Probe /me on mount — the cookie may already be valid (refresh, new tab).
  // /auth/me echoes the session's CSRF token so writes work in a fresh tab
  // whose sessionStorage hasn't seen a /login response yet.
  useEffect(() => {
    api<{ user: AuthUser; csrfToken: string }>('/auth/me')
      .then((res) => {
        setCsrfToken(res.csrfToken);
        setUser(res.user);
      })
      .catch(() => {
        setCsrfToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe: boolean) => {
    const res = await api<{ user: AuthUser; csrfToken: string }>('/auth/login', {
      method: 'POST',
      body: { email, password, rememberMe },
    });
    setCsrfToken(res.csrfToken);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Idempotent on the client even if the server rejects.
      if (!(err instanceof ApiError)) throw err;
    }
    setCsrfToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {sessionExpired ? <SessionExpired /> : children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
